import type {
  ArtifactRef,
  RunAggregateProjectionV2,
  RunAttempt,
  RunEvent,
  RunStatus,
  RunStepResult,
  WebTestRun
} from '@blume/web-test-model'

export type Section = 'runs' | 'pull-requests' | 'suites' | 'scenarios' | 'operations'
export type StatusFilter = 'all' | RunStatus | 'running'
export type TimeFilter = 'all' | '24h' | '7d' | '30d'
export type SavedView =
  | 'all'
  | 'active'
  | 'needs-attention'
  | 'my-runs'
  | 'pr-runs'
  | 'recent-failures'
export type SortOrder = 'newest' | 'oldest' | 'duration-desc' | 'duration-asc' | 'status'
export type EvidenceTab = 'evidence' | 'timeline' | 'raw'
export type AggregateStatus =
  | 'active'
  | 'passed'
  | 'failed'
  | 'needs-review'
  | 'infrastructure-error'
  | 'timed-out'
  | 'cancelled'

export interface RunFilters {
  classification: string
  target: string
  origin: string
  suite: string
  scenario: string
  branch: string
  pullRequest: string
  time: TimeFilter
}

export interface DetailFocus {
  attemptId: string
  stepId: string
  artifactId: string
  tab: EvidenceTab
}

export interface AggregateProgress {
  total: number
  counts: Record<AggregateStatus, number>
  summary: string
  firstActionableRunId?: string
  primaryRunId?: string
}

const dashboardSections = new Set<Section>([
  'runs',
  'pull-requests',
  'suites',
  'scenarios',
  'operations'
])

export const aggregateStatuses: AggregateStatus[] = [
  'active',
  'passed',
  'failed',
  'needs-review',
  'infrastructure-error',
  'timed-out',
  'cancelled'
]

export function parseDashboardRoute(hash: string): { section: Section; runId?: string } {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean)
  const section = dashboardSections.has(parts[0] as Section) ? (parts[0] as Section) : 'runs'
  return { section, ...(parts[1] ? { runId: parts[1] } : {}) }
}

export function parseDetailFocus(search: string): Partial<DetailFocus> {
  const parameters = new URLSearchParams(search)
  const tab = parameters.get('tab')
  return {
    ...(parameters.get('attempt') ? { attemptId: parameters.get('attempt')! } : {}),
    ...(parameters.get('step') ? { stepId: parameters.get('step')! } : {}),
    ...(parameters.get('artifact') ? { artifactId: parameters.get('artifact')! } : {}),
    ...(tab === 'evidence' || tab === 'timeline' || tab === 'raw' ? { tab } : {})
  }
}

export function resolveRunFocus(run: WebTestRun, search = ''): DetailFocus {
  const requested = parseDetailFocus(search)
  const requestedStepOwner = requested.stepId
    ? run.attempts.find((attempt) => attempt.steps.some((step) => step.id === requested.stepId))
    : undefined
  const requestedArtifact = requested.artifactId
    ? run.artifacts.find((artifact) => artifact.id === requested.artifactId)
    : undefined
  const artifactStepOwner = requestedArtifact
    ? run.attempts.find((attempt) =>
        attempt.steps.some((step) => step.artifactIds.includes(requestedArtifact.id))
      )
    : undefined
  const diagnosisStepId = run.diagnosis?.evidenceStepIds[0]
  const diagnosisStepOwner = diagnosisStepId
    ? run.attempts.find((attempt) => attempt.steps.some((step) => step.id === diagnosisStepId))
    : undefined
  const failedAttempt = [...run.attempts]
    .reverse()
    .find((attempt) => attempt.steps.some((step) => step.status === 'failed'))
  const requestedAttempt = run.attempts.find((attempt) => attempt.id === requested.attemptId)
  const attempt =
    requestedStepOwner ??
    artifactStepOwner ??
    requestedAttempt ??
    diagnosisStepOwner ??
    failedAttempt ??
    run.attempts.at(-1)
  const requestedStep = attempt?.steps.find((step) => step.id === requested.stepId)
  const artifactStep = requestedArtifact
    ? attempt?.steps.find((step) => step.artifactIds.includes(requestedArtifact.id))
    : undefined
  const step =
    requestedStep ??
    artifactStep ??
    attempt?.steps.find((candidate) => candidate.id === diagnosisStepId) ??
    attempt?.steps.find((candidate) => candidate.status === 'failed') ??
    attempt?.steps[0]
  const stepArtifacts = new Set(step?.artifactIds ?? [])
  const requestedArtifactIsInScope =
    requestedArtifact !== undefined && (!step || stepArtifacts.has(requestedArtifact.id))
  const artifact =
    (requestedArtifactIsInScope ? requestedArtifact : undefined) ??
    run.artifacts.find(
      (candidate) => stepArtifacts.has(candidate.id) && candidate.kind === 'screenshot-failure'
    ) ??
    run.artifacts.find((candidate) => stepArtifacts.has(candidate.id)) ??
    (!step
      ? (run.artifacts.find((candidate) => candidate.kind === 'screenshot-failure') ??
        run.artifacts.find((candidate) => candidate.kind.startsWith('screenshot-')))
      : undefined)

  return {
    attemptId: attempt?.id ?? '',
    stepId: step?.id ?? '',
    artifactId: artifact?.id ?? '',
    tab: requested.tab ?? 'evidence'
  }
}

export function searchWithDetailFocus(search: string, focus: DetailFocus): string {
  const parameters = new URLSearchParams(search)
  for (const key of ['attempt', 'step', 'artifact', 'tab']) parameters.delete(key)
  if (focus.attemptId) parameters.set('attempt', focus.attemptId)
  if (focus.stepId) parameters.set('step', focus.stepId)
  if (focus.artifactId) parameters.set('artifact', focus.artifactId)
  if (focus.tab !== 'evidence') parameters.set('tab', focus.tab)
  return parameters.size > 0 ? `?${parameters}` : ''
}

export function findArtifactOwner(
  run: WebTestRun,
  artifactId: string,
  preferredAttemptId: string
): { attempt: RunAttempt; step: RunStepResult } | undefined {
  const owners: Array<{ attempt: RunAttempt; step: RunStepResult }> = []
  for (const attempt of run.attempts) {
    for (const step of attempt.steps) {
      if (step.artifactIds.includes(artifactId)) owners.push({ attempt, step })
    }
  }
  return owners.find((owner) => owner.attempt.id === preferredAttemptId) ?? owners[0]
}

export function preservePaginationProgress(
  current: number | undefined,
  page: { runs: WebTestRun[]; pagination: { limit: number } }
): number | undefined {
  if (current === undefined) return undefined
  const firstPageNext =
    page.runs.length === page.pagination.limit ? page.pagination.limit : undefined
  if (firstPageNext === undefined) return current > 0 ? current : undefined
  return Math.max(current, firstPageNext)
}

export function candidateResponseIsCurrent(
  requestedRunId: string,
  currentRunId: string,
  requestId: number,
  currentRequestId: number
): boolean {
  return requestedRunId === currentRunId && requestId === currentRequestId
}

export function aggregateProgress(runs: WebTestRun[]): AggregateProgress {
  const counts: Record<AggregateStatus, number> = {
    active: 0,
    passed: 0,
    failed: 0,
    'needs-review': 0,
    'infrastructure-error': 0,
    'timed-out': 0,
    cancelled: 0
  }
  for (const run of runs) {
    if (run.state !== 'terminal') {
      counts.active += 1
    } else if (run.status && run.status in counts) {
      counts[run.status as Exclude<AggregateStatus, 'active'>] += 1
    }
  }
  const actionable = [...runs]
    .filter((run) => run.state === 'terminal' && run.status !== 'passed')
    .sort((left, right) => actionableRunRank(left) - actionableRunRank(right))[0]
  const firstActive = runs.find((run) => run.state !== 'terminal')
  const latest = [...runs].sort(
    (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
  )[0]
  return {
    total: runs.length,
    counts,
    summary: summarizeAggregateCounts(counts),
    ...(actionable?.id ? { firstActionableRunId: actionable.id } : {}),
    ...((actionable ?? firstActive ?? latest)?.id
      ? { primaryRunId: (actionable ?? firstActive ?? latest)!.id }
      : {})
  }
}

export function progressFromDurableAggregate(
  aggregate: RunAggregateProjectionV2
): AggregateProgress {
  const counts: Record<AggregateStatus, number> = {
    active: aggregate.active,
    passed: aggregate.passed,
    failed: aggregate.failed,
    'needs-review': aggregate.needsReview,
    'infrastructure-error': aggregate.infrastructureErrors,
    'timed-out': aggregate.timedOut,
    cancelled: aggregate.cancelled
  }
  // The model always supplies actionRunId for a non-empty aggregate, even
  // when every run passed. It is the best navigation run, not necessarily a
  // run requiring action. Only expose it as actionable when a terminal
  // non-pass outcome actually exists.
  const hasActionableOutcome =
    counts.failed +
      counts['needs-review'] +
      counts['infrastructure-error'] +
      counts['timed-out'] +
      counts.cancelled >
    0
  return {
    total: aggregate.total,
    counts,
    summary: summarizeAggregateCounts(counts),
    ...(hasActionableOutcome && aggregate.actionRunId
      ? { firstActionableRunId: aggregate.actionRunId }
      : {}),
    ...(aggregate.actionRunId || aggregate.latestRunId
      ? { primaryRunId: aggregate.actionRunId ?? aggregate.latestRunId }
      : {})
  }
}

export function groupPullRequestRuns(runs: WebTestRun[]): Array<{
  key: string
  label: string
  sourceSha?: string
  repository?: string
  pullRequest?: number
  runs: WebTestRun[]
}> {
  return groupRuns(
    runs.filter((run) => run.source.pullRequest),
    (run) => {
      const revision = run.source.sha ?? run.suiteRunId ?? run.id
      return `${run.source.repository ?? 'unknown-repository'}:${run.source.pullRequest}:${revision}`
    },
    (run) => `PR #${run.source.pullRequest}`
  )
    .map((group) => {
      const latest = [...group.runs].sort(
        (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
      )[0]!
      return {
        ...group,
        repository: latest.source.repository,
        pullRequest: latest.source.pullRequest
      }
    })
    .sort(
      (left, right) =>
        Math.max(...right.runs.map((run) => new Date(run.createdAt).getTime())) -
        Math.max(...left.runs.map((run) => new Date(run.createdAt).getTime()))
    )
}

export function stalePullRequestGroupKeys(
  groups: Array<{
    key: string
    repository?: string
    pullRequest?: number
  }>
): Set<string> {
  const seenPullRequests = new Set<string>()
  const staleGroups = new Set<string>()
  for (const group of groups) {
    if (!group.repository || !group.pullRequest) continue
    const identity = `${group.repository}:${group.pullRequest}`
    if (seenPullRequests.has(identity)) staleGroups.add(group.key)
    else seenPullRequests.add(identity)
  }
  return staleGroups
}

export function titleCase(value: string): string {
  return value
    .split('-')
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ')
}

export function stepPhaseLabel(step: RunStepResult): 'Setup' | 'Test' | 'Cleanup' {
  if (step.phase === 'setup' || step.id.startsWith('setup:')) return 'Setup'
  if (step.phase === 'cleanup' || step.id.startsWith('cleanup:')) return 'Cleanup'
  return 'Test'
}

export function formatDuration(milliseconds: number): string {
  if (milliseconds < 1_000) return `${milliseconds}ms`
  const seconds = Math.round(milliseconds / 1_000)
  if (seconds < 60) return `${seconds}s`
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

export function filterRuns(
  runs: WebTestRun[],
  options: {
    query: string
    status: StatusFilter
    savedView: SavedView
    currentUser?: string
    extra: RunFilters
    now?: number
  }
): WebTestRun[] {
  const normalized = options.query.trim().toLowerCase()
  const now = options.now ?? Date.now()
  return runs.filter((run) => {
    const matchesSearch =
      normalized.length === 0 ||
      run.id.toLowerCase().includes(normalized) ||
      run.scenarioId.toLowerCase().includes(normalized) ||
      run.suiteRunId?.toLowerCase().includes(normalized) ||
      run.source.branch?.toLowerCase().includes(normalized) ||
      run.source.sha?.toLowerCase().includes(normalized) ||
      run.initiatedBy.toLowerCase().includes(normalized) ||
      String(run.source.pullRequest ?? '').includes(normalized)
    const matchesStatus =
      options.status === 'all' ||
      (options.status === 'running' ? run.state !== 'terminal' : run.status === options.status)
    const matchesTime =
      options.extra.time === 'all' ||
      new Date(run.createdAt).getTime() >= now - timeWindowMs(options.extra.time)
    return (
      matchesSearch &&
      matchesStatus &&
      matchesTime &&
      matchesSavedView(run, options.savedView, options.currentUser, now) &&
      (!options.extra.classification || run.classification === options.extra.classification) &&
      (!options.extra.target || run.target === options.extra.target) &&
      (!options.extra.origin || run.origin === options.extra.origin) &&
      (!options.extra.suite ||
        run.suiteRunId?.toLowerCase().includes(options.extra.suite.toLowerCase())) &&
      (!options.extra.scenario ||
        run.scenarioId.toLowerCase().includes(options.extra.scenario.toLowerCase())) &&
      (!options.extra.branch ||
        run.source.branch?.toLowerCase().includes(options.extra.branch.toLowerCase())) &&
      (!options.extra.pullRequest ||
        String(run.source.pullRequest ?? '') === options.extra.pullRequest.trim())
    )
  })
}

export function sortRuns(runs: WebTestRun[], order: SortOrder): WebTestRun[] {
  const sorted = [...runs]
  const created = (run: WebTestRun): number => new Date(run.createdAt).getTime()
  const duration = (run: WebTestRun): number => run.durationMs ?? 0
  if (order === 'oldest') return sorted.sort((left, right) => created(left) - created(right))
  if (order === 'duration-desc') {
    return sorted.sort((left, right) => duration(right) - duration(left))
  }
  if (order === 'duration-asc') {
    return sorted.sort((left, right) => duration(left) - duration(right))
  }
  if (order === 'status') {
    const priority = (run: WebTestRun): number => {
      if (run.state !== 'terminal') return 0
      return {
        failed: 1,
        'needs-review': 2,
        'infrastructure-error': 3,
        'timed-out': 4,
        cancelled: 5,
        passed: 6
      }[run.status ?? 'passed']
    }
    return sorted.sort(
      (left, right) => priority(left) - priority(right) || created(right) - created(left)
    )
  }
  return sorted.sort((left, right) => created(right) - created(left))
}

export function mergeRunWithoutRegression(current: WebTestRun[], next: WebTestRun): WebTestRun[] {
  const index = current.findIndex((run) => run.id === next.id)
  if (index < 0) return [next, ...current]
  const existing = current[index]!
  if (existing.state === 'terminal' && next.state !== 'terminal') return current
  const copy = [...current]
  copy[index] = next
  return copy
}

export function mergeRunListsWithoutRegression(
  current: WebTestRun[],
  next: WebTestRun[]
): WebTestRun[] {
  const existing = new Map(current.map((run) => [run.id, run]))
  const nextIds = new Set(next.map((run) => run.id))
  const merged = next.map((run) => {
    const prior = existing.get(run.id)
    return prior?.state === 'terminal' && run.state !== 'terminal' ? prior : run
  })
  return [...merged, ...current.filter((run) => !nextIds.has(run.id))]
}

export function mergeEvents(current: RunEvent[], next: RunEvent[]): RunEvent[] {
  const bySequence = new Map(current.map((event) => [event.sequence, event]))
  for (const event of next) bySequence.set(event.sequence, event)
  return [...bySequence.values()].sort((left, right) => left.sequence - right.sequence)
}

export function preferredArtifactId(run: WebTestRun, step: RunStepResult): string {
  const artifactIds = new Set(step.artifactIds)
  const artifacts = run.artifacts.filter((artifact) => artifactIds.has(artifact.id))
  return (
    artifacts.find((artifact) => artifact.kind === 'screenshot-failure')?.id ??
    artifacts.find((artifact) => artifact.kind.startsWith('screenshot-'))?.id ??
    artifacts[0]?.id ??
    ''
  )
}

export function artifactRetentionLabel(artifacts: ArtifactRef[], now = Date.now()): string {
  const expirations: number[] = []
  for (const artifact of artifacts) {
    if (!artifact.expiresAt) continue
    const expiration = new Date(artifact.expiresAt).getTime()
    if (Number.isFinite(expiration)) expirations.push(expiration)
  }
  if (expirations.length === 0) return 'No expiry reported'
  const lastExpiry = Math.max(...expirations)
  if (lastExpiry <= now) return 'Expired'
  return `Until ${new Date(lastExpiry).toLocaleDateString()}`
}

function summarizeAggregateCounts(counts: Record<AggregateStatus, number>): string {
  return (
    aggregateStatuses
      .filter((status) => counts[status] > 0)
      .map((status) => `${counts[status]} ${titleCase(status).toLowerCase()}`)
      .join(' · ') || 'No runs'
  )
}

function actionableRunRank(run: WebTestRun): number {
  if (run.status === 'failed') return 0
  if (run.status === 'needs-review') return 1
  if (run.status === 'timed-out') return 2
  if (run.status === 'infrastructure-error') return 3
  if (run.status === 'cancelled') return 4
  return 5
}

export function groupRuns(
  runs: WebTestRun[],
  keyForRun: (run: WebTestRun) => string,
  labelForRun: (run: WebTestRun) => string = keyForRun
): Array<{ key: string; label: string; sourceSha?: string; runs: WebTestRun[] }> {
  const groups = new Map<string, WebTestRun[]>()
  for (const run of runs) {
    const key = keyForRun(run)
    groups.set(key, [...(groups.get(key) ?? []), run])
  }
  return [...groups.entries()]
    .map(([key, groupedRuns]) => ({
      key,
      label: labelForRun(groupedRuns[0]!),
      sourceSha: groupedRuns[0]?.source.sha,
      runs: groupedRuns
    }))
    .sort((left, right) => right.runs.length - left.runs.length)
}

function timeWindowMs(filter: Exclude<TimeFilter, 'all'>): number {
  const day = 24 * 60 * 60 * 1_000
  return filter === '24h' ? day : filter === '7d' ? 7 * day : 30 * day
}

function matchesSavedView(
  run: WebTestRun,
  view: SavedView,
  currentUser: string | undefined,
  now: number
): boolean {
  if (view === 'active') return run.state !== 'terminal'
  if (view === 'needs-attention') {
    return run.state === 'terminal' && run.status !== 'passed' && run.status !== 'cancelled'
  }
  if (view === 'my-runs') return currentUser ? run.initiatedBy === currentUser : true
  if (view === 'pr-runs') return Boolean(run.source.pullRequest)
  if (view === 'recent-failures') {
    return run.status === 'failed' && new Date(run.createdAt).getTime() >= now - timeWindowMs('7d')
  }
  return true
}
